import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Drawer, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../reduxStore/Store';
import {
  clearAssistantError, confirmAssistantAction, conversationLoaded, newConversation,
  sendAssistantMessage, setAssistantOpen,
} from '../../reduxStore/AssistantSlice';
import { ChatMessage, ChatPendingConfirmation } from '../../models/models';
import { assistantStorageKey, assistantToolLabel } from '../../constants/assistantTools';
import { labels } from '../../constants/labels';
import AssistantMarkdown from './AssistantMarkdown';

const MAX_STORED_MESSAGES = 200;

const SUGGESTIONS = [
  `Combien de ${labels.clientPluriel.toLowerCase()} avons-nous ?`,
  'Quels articles sont en rupture de stock ?',
  "Quel est le chiffre d'affaires de ce mois ?",
];

/** Garde les derniers messages en coupant sur une question (l'historique doit commencer par l'utilisateur). */
function trimForStorage(messages: ChatMessage[]): ChatMessage[] {
  if (messages.length <= MAX_STORED_MESSAGES) return messages;
  const start = messages.findIndex((m, i) => i >= messages.length - MAX_STORED_MESSAGES && m.role === 'user');
  return start === -1 ? [] : messages.slice(start);
}

function loadConversation(userId: number): { messages: ChatMessage[]; pending: ChatPendingConfirmation[] | null } {
  try {
    const raw = localStorage.getItem(assistantStorageKey(userId));
    if (!raw) return { messages: [], pending: null };
    const parsed = JSON.parse(raw);
    const messages = Array.isArray(parsed?.messages) ? parsed.messages : [];
    const pending = Array.isArray(parsed?.pending) && parsed.pending.every((p: ChatPendingConfirmation) => p.expiresAt > Date.now())
      ? parsed.pending
      : null;
    return { messages, pending };
  } catch {
    return { messages: [], pending: null };
  }
}

/** Valeur lisible d'un paramètre d'écriture (confirmation). */
function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return `${value.length} élément(s)`;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// ── Sous-composants ──────────────────────────────────────────────────────────

const ToolChips: React.FC<{ names: string[]; running?: boolean }> = ({ names, running }) => (
  <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 0.5 }}>
    {names.map((name, i) => (
      <Chip
        key={`${name}-${i}`}
        size="small"
        icon={running ? <CircularProgress size={12} /> : <CheckCircleOutlineIcon />}
        label={assistantToolLabel(name)}
        sx={{ bgcolor: '#eef4ff', color: '#2563eb', fontSize: '0.72rem', '& .MuiChip-icon': { color: '#2563eb' } }}
      />
    ))}
  </Stack>
);

const Bubble: React.FC<{ fromUser?: boolean; children: React.ReactNode }> = ({ fromUser, children }) => (
  <Box sx={{ display: 'flex', justifyContent: fromUser ? 'flex-end' : 'flex-start', mb: 1.5 }}>
    <Box
      sx={{
        maxWidth: '88%',
        px: 1.5,
        py: 1,
        borderRadius: 2,
        ...(fromUser
          ? { bgcolor: '#2563eb', color: '#fff', borderBottomRightRadius: 4, whiteSpace: 'pre-wrap', fontSize: '0.875rem' }
          : { bgcolor: '#fff', border: '1px solid #e4e7ec', borderBottomLeftRadius: 4 }),
      }}
    >
      {children}
    </Box>
  </Box>
);

const ConfirmationCard: React.FC<{
  calls: ChatPendingConfirmation[];
  disabled: boolean;
  onDecide: (approved: boolean) => void;
}> = ({ calls, disabled, onDecide }) => (
  <Box sx={{ border: '1px solid #f79009', bgcolor: '#fffaeb', borderRadius: 2, p: 1.5, mb: 1.5 }}>
    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
      <WarningAmberIcon sx={{ color: '#dc6803' }} fontSize="small" />
      <Typography variant="subtitle2" sx={{ color: '#101828' }}>Confirmation requise</Typography>
    </Stack>
    {calls.map((call) => (
      <Box key={call.toolCallId} sx={{ mb: 1 }}>
        <Typography variant="body2" fontWeight={600} sx={{ color: '#101828' }}>
          {assistantToolLabel(call.name)}
        </Typography>
        {Object.entries(call.input).map(([key, value]) => (
          <Typography key={key} variant="caption" component="div" sx={{ color: '#667085' }}>
            {key} : {formatValue(value)}
          </Typography>
        ))}
      </Box>
    ))}
    <Stack direction="row" justifyContent="flex-end" gap={1}>
      <Button size="small" color="inherit" disabled={disabled} onClick={() => onDecide(false)}>Annuler</Button>
      <Button size="small" variant="contained" disabled={disabled} onClick={() => onDecide(true)}>Confirmer</Button>
    </Stack>
  </Box>
);

// ── Composant principal ──────────────────────────────────────────────────────

/**
 * Bouton « Assistant » de la Navbar + Drawer de conversation.
 * Visible uniquement avec la permission `assistant.use` (revérifiée côté serveur).
 */
const AssistantDrawer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { open, messages, streamingText, activeTools, pending, loading, error } =
    useSelector((state: RootState) => state.AssistantSlice);
  const { currentUser, permissions } = useSelector((state: RootState) => state.AuthSlice);
  const selectedDepartement = useSelector((state: RootState) => state.AppSlice.selectedDepartement);

  const [input, setInput] = useState('');
  const requestRef = useRef<{ abort: () => void } | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const hydratedFor = useRef<number | null>(null);

  const userId = currentUser?.id;
  const allowed = permissions.includes('assistant.use');

  // Restauration de la conversation de CET utilisateur.
  useEffect(() => {
    if (!userId || hydratedFor.current === userId) return;
    dispatch(conversationLoaded(loadConversation(userId)));
    hydratedFor.current = userId;
  }, [dispatch, userId]);

  // Sauvegarde locale à chaque changement (jamais côté serveur).
  useEffect(() => {
    if (!userId || hydratedFor.current !== userId) return;
    try {
      localStorage.setItem(assistantStorageKey(userId), JSON.stringify({ messages: trimForStorage(messages), pending }));
    } catch {
      /* quota / navigation privée : la conversation reste en mémoire */
    }
  }, [messages, pending, userId]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [open, messages.length, streamingText, activeTools.length, pending]);

  if (!allowed) return null;

  const departement_nom = selectedDepartement?.nom;

  const send = (text: string) => {
    const question = text.trim();
    if (!question || loading) return;
    setInput('');
    requestRef.current = dispatch(sendAssistantMessage({ text: question, departement_nom }));
  };

  const decide = (approved: boolean) => {
    if (!pending || loading) return;
    requestRef.current = dispatch(confirmAssistantAction({ approved, pending, departement_nom }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const visible = messages.filter((m) => m.role !== 'tool');

  return (
    <>
      <Tooltip title="Assistant IA">
        <IconButton color="inherit" size="small" onClick={() => dispatch(setAssistantOpen(true))}>
          <AutoAwesomeIcon />
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => dispatch(setAssistantOpen(false))}
        PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, bgcolor: '#f4f5f7', display: 'flex', flexDirection: 'column' } }}
      >
        {/* En-tête */}
        <Stack direction="row" alignItems="center" gap={1.5} sx={{ px: 2, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid #e4e7ec' }}>
          <Box sx={{ bgcolor: '#eef4ff', color: '#2563eb', borderRadius: 2, p: 0.75, display: 'flex' }}>
            <AutoAwesomeIcon fontSize="small" />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#101828', lineHeight: 1.2 }}>Assistant</Typography>
            <Typography variant="caption" sx={{ color: '#667085' }} noWrap>
              {departement_nom ? `Département : ${departement_nom}` : 'Vos données, avec vos droits'}
            </Typography>
          </Box>
          <Tooltip title="Nouvelle conversation">
            <span>
              <IconButton size="small" disabled={loading || messages.length === 0} onClick={() => dispatch(newConversation())}>
                <AddCommentOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={() => dispatch(setAssistantOpen(false))}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Conversation */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 2 }}>
          {visible.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="body2" sx={{ color: '#667085', mb: 2 }}>
                Posez une question sur vos données : {labels.tiersPluriel.toLowerCase()}, stock, ventes, comptes…
              </Typography>
              <Stack gap={1} alignItems="center">
                {SUGGESTIONS.map((s) => (
                  <Chip key={s} label={s} onClick={() => send(s)} variant="outlined" sx={{ bgcolor: '#fff', maxWidth: '100%' }} />
                ))}
              </Stack>
            </Box>
          )}

          {visible.map((m, i) =>
            m.role === 'user' ? (
              <Bubble key={i} fromUser>{m.text}</Bubble>
            ) : m.role === 'assistant' ? (
              <Box key={i}>
                {m.toolCalls.length > 0 && <ToolChips names={m.toolCalls.map((c) => c.name)} />}
                {m.text && <Bubble><AssistantMarkdown text={m.text} /></Bubble>}
              </Box>
            ) : null,
          )}

          {loading && activeTools.length > 0 && <ToolChips names={activeTools.map((t) => t.name)} running />}
          {loading && streamingText && <Bubble><AssistantMarkdown text={streamingText} /></Bubble>}
          {loading && !streamingText && activeTools.length === 0 && (
            <Box sx={{ mb: 1.5 }}><CircularProgress size={18} /></Box>
          )}

          {pending && <ConfirmationCard calls={pending} disabled={loading} onDecide={decide} />}

          {error && (
            <Alert severity="error" onClose={() => dispatch(clearAssistantError())} sx={{ mb: 1.5 }}>{error}</Alert>
          )}
          <div ref={bottomRef} />
        </Box>

        {/* Saisie */}
        <Stack direction="row" alignItems="flex-end" gap={1} sx={{ p: 1.5, bgcolor: '#fff', borderTop: '1px solid #e4e7ec' }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder={pending ? 'Ou posez une autre question…' : 'Votre question…'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          {loading ? (
            <Tooltip title="Arrêter">
              <IconButton color="error" onClick={() => requestRef.current?.abort()}><StopIcon /></IconButton>
            </Tooltip>
          ) : (
            <IconButton color="primary" disabled={!input.trim()} onClick={() => send(input)}><SendIcon /></IconButton>
          )}
        </Stack>
      </Drawer>
    </>
  );
};

export default AssistantDrawer;
